import { createHoverIdleNavIcon } from "@/app/game/navigationIcons";
import type { NavItemDef } from "@/app/gameNavigation";
import { getMessages } from "@/i18n/runtime";

export interface NavigationElements {
  readonly root: HTMLElement;
  readonly buttons: ReadonlyMap<string, HTMLButtonElement>;
  readonly cleanups: readonly (() => void)[];
}

export interface CreateNavigationElementsOptions {
  readonly isTouchPrimary: boolean;
  readonly items: readonly NavItemDef[];
  readonly activeScreenId: string;
  readonly switchScreen: (id: string) => void;
  readonly ensureSettingsPanel: () => void;
  readonly standbyToggle?: HTMLButtonElement;
}

export function createNavigationElements(
  options: CreateNavigationElementsOptions,
): NavigationElements {
  const messages = getMessages();
  const root = document.createElement("nav");
  root.className = "hover-idle-nav-position hover-idle-nav";
  root.setAttribute("aria-label", messages.navigation.sectionsAria);

  const cleanups: Array<() => void> = [];
  const buttons = new Map<string, HTMLButtonElement>();

  for (const item of options.items) {
    const button = createNavigationButton(
      item,
      options.activeScreenId,
      options.isTouchPrimary,
    );
    const ensureScreenContent = (): void => {
      if (item.id === "settings") {
        options.ensureSettingsPanel();
      }
    };
    const handler = (): void => {
      ensureScreenContent();
      options.switchScreen(item.id);
    };
    if (item.click) {
      button.addEventListener("click", handler);
      cleanups.push(() => button.removeEventListener("click", handler));
    } else {
      const touchHandler = (event: PointerEvent): void => {
        if (event.pointerType !== "mouse") {
          handler();
        }
      };
      button.addEventListener("pointerenter", handler);
      button.addEventListener("focus", handler);
      button.addEventListener("pointerdown", touchHandler);
      cleanups.push(() => {
        button.removeEventListener("pointerenter", handler);
        button.removeEventListener("focus", handler);
        button.removeEventListener("pointerdown", touchHandler);
      });
    }

    buttons.set(item.id, button);
    root.append(button);
  }

  if (options.standbyToggle !== undefined) {
    root.append(options.standbyToggle);
  }

  return { root, buttons, cleanups };
}

function createNavigationButton(
  item: NavItemDef,
  activeScreenId: string,
  isTouchPrimary: boolean,
): HTMLButtonElement {
  const messages = getMessages();
  const button = document.createElement("button");
  button.type = "button";
  button.className = "hover-idle-nav-item";
  if (item.draft) {
    button.classList.add("hover-idle-nav-draft");
    button.hidden = true;
  }
  if (item.icon) {
    button.setAttribute("aria-label", item.label);
    button.title = item.label;
    button.append(createHoverIdleNavIcon(item.icon));
  } else {
    button.textContent = item.label;
  }
  button.dataset.screen = item.id;
  button.dataset.active = item.id === activeScreenId ? "true" : "false";
  if (item.click) {
    button.dataset.click = "true";
    const hint = document.createElement("span");
    hint.className = "hover-idle-nav-click-hint";
    hint.textContent = isTouchPrimary
      ? messages.navigation.tapHint
      : messages.navigation.clickHint;
    hint.setAttribute("aria-hidden", "true");
    button.append(hint);
  }
  return button;
}

export function syncNavigationActiveState(
  buttons: ReadonlyMap<string, HTMLButtonElement>,
  activeScreenId: string,
): void {
  for (const [id, button] of buttons) {
    button.dataset.active = id === activeScreenId ? "true" : "false";
  }
}
